/**
 * Analytics — Photos API Contracts
 *
 * Wire format for PhotosController and KioskPhotosController endpoints.
 *
 * Note on paymentMethod enum:
 *   Server-side uses 'WRISTBAND_CREDIT' (singular). This is the authoritative
 *   spelling. Any caller using 'WRISTBAND_CREDITS' (plural) must be updated.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const CapturePhotoRequestSchema = z.object({
  attractionId: z.string().min(1),
  captureTime: z.string().datetime({ offset: true }).or(z.string().min(1)),
  photoUrl: z.string().min(1),
  s3Key: z.string().min(1),
  thumbnailUrl: z.string().optional(),
});
export type CapturePhotoRequest = z.infer<typeof CapturePhotoRequestSchema>;

export const GetCustomerPhotosQuerySchema = z.object({
  wristbandId: z.string().min(1),
});
export type GetCustomerPhotosQuery = z.infer<typeof GetCustomerPhotosQuerySchema>;

export const PurchasePhotoRequestSchema = z.object({
  wristbandId: z.string().min(1),
});
export type PurchasePhotoRequest = z.infer<typeof PurchasePhotoRequestSchema>;

export const KioskGetPhotosQuerySchema = z.object({
  wristbandId: z.string().min(1),
});
export type KioskGetPhotosQuery = z.infer<typeof KioskGetPhotosQuerySchema>;

export const KioskPurchaseBatchRequestSchema = z.object({
  wristbandId: z.string().min(1),
  photoIds: z.array(z.string().min(1)).min(1),
  paymentMethod: z.enum(['WRISTBAND_CREDIT', 'EDC']),
});
export type KioskPurchaseBatchRequest = z.infer<typeof KioskPurchaseBatchRequestSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const PhotoRecordSchema = z.object({
  id: z.string(),
  attractionId: z.string(),
  captureTime: z.string().or(z.date()),
  photoUrl: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  s3Key: z.string().optional(),
  wristbandId: z.string().nullable().optional(),
  customerId: z.string().nullable().optional(),
  isPurchased: z.boolean(),
  purchasedAt: z.string().or(z.date()).nullable().optional(),
  creditCost: z.number().optional(),
  expiresAt: z.string().or(z.date()).optional(),
});
export type PhotoRecord = z.infer<typeof PhotoRecordSchema>;

export const KioskPhotoItemSchema = z.object({
  id: z.string(),
  attractionId: z.string(),
  attractionName: z.string().nullable().optional(),
  capturedAt: z.string().or(z.date()),
  photoUrl: z.string(),
  thumbnailUrl: z.string().nullable(),
  isPurchased: z.boolean(),
  price: z.number(),
  downloadUrl: z.string().nullable(),
});
export type KioskPhotoItem = z.infer<typeof KioskPhotoItemSchema>;

export const KioskPurchaseBatchResponseSchema = z.object({
  purchased: z.number().int(),
  totalCreditCost: z.number(),
  photoIds: z.array(z.string()),
});
export type KioskPurchaseBatchResponse = z.infer<typeof KioskPurchaseBatchResponseSchema>;

export const GetDownloadUrlResponseSchema = z.object({
  downloadUrl: z.string(),
});
export type GetDownloadUrlResponse = z.infer<typeof GetDownloadUrlResponseSchema>;
